<script setup lang="ts">
/**
 * SidePanel — a right-hand drawer for editing one record from a list.
 *
 * Admin forms used to render *underneath* their table. With fifteen
 * assistants that means clicking "Edit" on row twelve appends a form ~1400px
 * below the fold: the page does not move, nothing indicates anything opened,
 * and the row you were working on is nowhere near the fields you are
 * editing. Scrolling to it would only paper over the shape of the problem.
 *
 * A drawer fixes the shape: the list stays on screen as context (so you can
 * see which record is selected and move to the next one), the form owns its
 * own scroll, and Save/Cancel stay pinned within reach no matter how long the
 * form gets. Preferred over a modal here because the list *is* the context —
 * you are usually working down it, not visiting one record in isolation.
 *
 * Behaviour: Escape closes, the scrim closes, focus moves in on open and
 * returns to the trigger on close, Tab is trapped inside, and the page behind
 * is scroll-locked so the list does not drift under the drawer.
 */
import { nextTick, onBeforeUnmount, ref, watch } from "vue";

const props = withDefaults(
  defineProps<{
    /** v-model — whether the drawer is showing. */
    modelValue: boolean;
    title: string;
    /** Small line under the title — usually the record's id. */
    subtitle?: string;
    /** Drawer width. Long forms want more; keep some list visible. */
    width?: string;
  }>(),
  { subtitle: "", width: "min(44rem, 92vw)" },
);

const emit = defineEmits<{ (e: "update:modelValue", open: boolean): void }>();

const panel = ref<HTMLElement | null>(null);
let restoreFocusTo: HTMLElement | null = null;

function close(): void {
  emit("update:modelValue", false);
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function onKeydown(e: KeyboardEvent): void {
  if (e.key === "Escape") {
    e.stopPropagation();
    close();
    return;
  }
  if (e.key !== "Tab" || !panel.value) return;
  const items = Array.from(
    panel.value.querySelectorAll<HTMLElement>(FOCUSABLE),
  ).filter((el) => el.offsetParent !== null);
  if (items.length === 0) return;
  const first = items[0];
  const last = items[items.length - 1];
  const active = document.activeElement as HTMLElement | null;
  if (e.shiftKey && (active === first || active === panel.value)) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && active === last) {
    e.preventDefault();
    first.focus();
  }
}

watch(
  () => props.modelValue,
  async (open) => {
    if (open) {
      restoreFocusTo = document.activeElement as HTMLElement | null;
      document.body.style.overflow = "hidden";
      await nextTick();
      // The panel itself, not the first field — landing in a text input
      // means a screen reader announces the field before the drawer's name.
      panel.value?.focus();
    } else {
      document.body.style.overflow = "";
      restoreFocusTo?.focus?.();
      restoreFocusTo = null;
    }
  },
);

onBeforeUnmount(() => {
  document.body.style.overflow = "";
});
</script>

<template>
  <Teleport to="body">
    <Transition name="side-panel">
      <div v-if="modelValue" class="side-panel__layer" @keydown="onKeydown">
        <div class="side-panel__scrim" @click="close"></div>
        <aside
          ref="panel"
          class="side-panel"
          role="dialog"
          aria-modal="true"
          :aria-label="title"
          tabindex="-1"
          :style="{ '--side-panel-w': width }"
        >
          <header class="side-panel__head">
            <div class="side-panel__titles">
              <h2 class="side-panel__title">{{ title }}</h2>
              <p v-if="subtitle" class="side-panel__subtitle">{{ subtitle }}</p>
            </div>
            <button
              type="button"
              class="side-panel__close"
              aria-label="Close"
              @click="close"
            >
              ×
            </button>
          </header>

          <div class="side-panel__body"><slot /></div>

          <!-- Pinned: a form long enough to scroll must not hide its own
               Save button somewhere off the bottom. -->
          <footer v-if="$slots.actions" class="side-panel__foot">
            <slot name="actions" />
          </footer>
        </aside>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.side-panel__layer {
  position: fixed;
  inset: 0;
  z-index: 120;
  display: flex;
  justify-content: flex-end;
}
.side-panel__scrim {
  position: absolute;
  inset: 0;
  background: color-mix(in srgb, var(--color-ink) 32%, transparent);
  backdrop-filter: blur(2px);
}
.side-panel {
  position: relative;
  display: flex;
  flex-direction: column;
  width: var(--side-panel-w);
  height: 100%;
  background: var(--color-surface);
  border-left: 1px solid var(--glass-border);
  box-shadow: var(--shadow-lg);
}
.side-panel:focus {
  outline: none;
}
.side-panel__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-3);
  flex: none;
  padding: var(--space-4) var(--space-5);
  border-bottom: 1px solid var(--color-border);
}
.side-panel__title {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 650;
  color: var(--color-ink);
}
.side-panel__subtitle {
  margin: 0.15rem 0 0;
  font-family: var(--font-mono);
  font-size: 0.78rem;
  color: var(--color-muted);
}
.side-panel__close {
  flex: none;
  display: grid;
  place-items: center;
  width: 2rem;
  height: 2rem;
  border-radius: var(--radius-md);
  background: transparent;
  color: var(--color-muted);
  font-size: 1.3rem;
  line-height: 1;
  cursor: pointer;
  transition:
    background var(--dur-1) var(--ease-out),
    color var(--dur-1) var(--ease-out);
}
.side-panel__close:hover {
  background: color-mix(in srgb, var(--color-ink) 6%, transparent);
  color: var(--color-ink);
}
.side-panel__body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: var(--space-5);
}
.side-panel__foot {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--space-2);
  flex: none;
  padding: var(--space-3) var(--space-5);
  border-top: 1px solid var(--color-border);
  background: var(--color-surface);
}

.side-panel-enter-active,
.side-panel-leave-active {
  transition: opacity var(--dur-2) var(--ease-out);
}
.side-panel-enter-active .side-panel,
.side-panel-leave-active .side-panel {
  transition: transform var(--dur-2) var(--ease-out);
}
.side-panel-enter-from,
.side-panel-leave-to {
  opacity: 0;
}
.side-panel-enter-from .side-panel,
.side-panel-leave-to .side-panel {
  transform: translateX(100%);
}
@media (prefers-reduced-motion: reduce) {
  .side-panel-enter-active .side-panel,
  .side-panel-leave-active .side-panel {
    transition: none;
  }
}
@media (max-width: 640px) {
  .side-panel {
    width: 100%;
    border-left: 0;
  }
}
</style>
